<template>
    <b-form
        ref="wfstForm"
        id="wfs-t-form"
        name="wfs-t-form"
        enctype="multipart/form-data"
        v-on:submit.prevent="$_onSubmit"
    >
        <!-- Form content -->
        <div id="wfst_panel_content">
            <pre v-if="$store.getters.showDevInfo">
                edit: {{ wfsTModify }}
                add: {{ wfsTAdd }}
                hasChangedShape: {{ hasChangedShape }}
                geometryType: {{ geometryType }}
            </pre>
            <!-- Non personal data -->
            <div v-for="field in orderedInformationAttributes" :key="field.id">
                <div
                    v-if="
                        (wfsTModify && field.visible) ||
                        !field.alias.includes('Foto')
                    "
                >
                    <!-- Only Alphanumeric and integer fields accepted -->
                    <div v-if="isMobile && field.dataType === isString">
                        <x-input
                            :id="field.attribute"
                            :name="field.attribute"
                            :text="$_getFieldTitle(field)"
                            :required="false"
                            :value="$_getValue(field)"
                            :maxlength="50"
                            v-model="field.value"
                            :justInvalidState="true"
                        >
                        </x-input>
                    </div>
                    <div v-if="field.dataType === isInt">
                        <x-input
                            :id="field.attribute"
                            :name="field.attribute"
                            :text="$_getFieldTitle(field)"
                            :required="false"
                            :value="$_getValue(field)"
                            :maxlength="50"
                            v-model="field.value"
                            :justInvalidState="true"
                        >
                        </x-input>
                    </div>
                </div>
            </div>
        </div>
        <!-- Buttons -->
        <div class="sticky-bottom fixed-bottom">
            <b-button
                v-if="showSaveButton"
                variant="primary"
                type="submit"
                :class="{ saving: isSaving }"
            >
                <span v-if="!isSaving">{{ saveButtonText }}</span>
                <span
                    :class="{ 'fas fa-sync fa-spin': isSaving }"
                    v-if="isSaving"
                ></span>
            </b-button>
            <b-button
                :disabled="isSaving"
                @click="$_onCancel"
                class="ml-2"
                :class="{ saving: isSaving }"
            >
                {{ $t('btnCancelLabel') }}
            </b-button>
        </div>
    </b-form>
</template>

<script>
    import { sortBy } from 'lodash';
    import { mapActions, mapGetters } from 'vuex';
    import { getCreateWfsTData } from '../../../../services/utilities';

    import {
        NEDGLOBE_VIEWER,
        DEFAULT_ANIMATION_MS,
        PANEL_WIDTHS
    } from '../../../../constants';

    import { isSmallDevice } from '../../../../services/utilities/devices';

    export default {
        name: 'WfsTForm',

        props: {
            layerThemeObject: { type: Object, required: true },
            layerObject: { type: Object, required: true },
            isEditing: { type: Boolean, default: false }
        },

        async created() {
            const localData = JSON.parse(JSON.stringify(this.layerThemeObject ?? {}));
            this.layerData = { layerTheme: { ...localData } };
            await this.$_initWfsTObject(this.layerObject);
            if (this.wfsTModify) {
                this.$_initializeModifyWfsTData();
            }
            if (this.wfsTAdd && this.orderedInformationAttributes.length == 0) {
                this.$_onSubmit();
            }
        },

        destroyed() {
            const customLayerName = NEDGLOBE_VIEWER.customLayers.drawing;
            this.$store.commit('RESET_MAP_FEATURES', {
                mapName: 'main',
                customLayerName
            });
        },

        data() {
            return {
                layerData: null,
                // variables
                lastwfsTAddress: null,

                // add id = -1
                featureId: '-1',

                error: null,
                isSaving: false,
                layerShape: null
            };
        },

        computed: {
            ...mapGetters([
                'isMobile',
                'getMainMap',
                'getUserData',
                'getUserInfo',
                'getOrganizationId',
                'getLocale',
                'getThemeById'
            ]),
            layerTheme() {
                return this.layerData?.layerTheme;
            },
            hasChangedShape() {
                return this.layerObject.updated === true;
            },
            wfsTModify() {
                return this.isEditing;
            },
            wfsTAdd() {
                return !this.wfsTModify;
            },
            geometryType() {
                return this.layerShape?.featureObject?.geometryType;
            },
            userInfo() {
                return this.isMobile ? this.getUserData : this.getUserInfo;
            },
            isString() {
                return 'xsd:string';
            },
            isInt() {
                return 'xsd:int';
            },
            /**
             * update infoAttributes array with prefilled values for teurgmeldingen in web
             * @return {array} [infoAttributes array]
             */
            infoAttributes() {
                if (this.layerObject) {
                    let infoAttributes =
                        this.layerThemeObject?.informationAttributes;
                    if (this.layerObject?.data) {
                        infoAttributes = infoAttributes?.map((field) => {
                            const DataFieldvalue =
                                this.layerObject.data[field.attribute];
                            if (DataFieldvalue) {
                                return { ...field, value: DataFieldvalue };
                            }
                            return { ...field, value: '' };
                        });
                    }
                    return infoAttributes;
                }
                return null;
            },

            /**
             * Sort feedbackFields array on its 'order' field
             * Only return attributes that are shown in detail form
             * @return {array} [ordered feedbackFields array]
             */
            orderedInformationAttributes() {
                if (this.infoAttributes) {
                    return sortBy(this.infoAttributes, [(o) => o.order]).filter(
                        (a) => a.displayInDetail
                    );
                }
                return [];
            },

            /**
             * Always show save button, except when it is a measurement with no modifiable fields
             * @return {Boolean} [true if save button must be shown, false otherwise]
             */
            showSaveButton() {
                return true;
            },

            /**
             * Text on Save button
             * @return {string} [text]
             */
            saveButtonText() {
                if (
                    this.layerTheme?.feedbackType &&
                    this.layerTheme.feedbackType.useInWebTheme &&
                    !this.isMobile
                ) {
                    return this.$t('buttonNameSend');
                } else {
                    return this.$t('btnSaveLabel');
                }
            }
        },
        watch: {
            async layerObject(newVal) {
                await this.$_initWfsTObject(newVal);
                if (this.wfsTModify && this.hasChangedShape) {
                    if (!this.lastwfsTAddress) {
                        this.$_onSubmit();
                    }
                }
            }
        },
        methods: {
            ...mapActions(['getAddressInfo']),
            async $_initWfsTObject(layerObject) {
                this.layerShape = layerObject;

                // Load wfst-layer if needed
                if (this.layerThemeObject) {
                    await this.$store.dispatch('loadTheme', {
                        themeId: this.layerThemeObject.themeId,
                        layerId: this.layerThemeObject.id,
                        toggledTheme: false
                    });
                    const { computedStyles } = this.$store.getters.getLayerById(
                        this.layerThemeObject.customId
                    ).extended;

                    const customLayerName = NEDGLOBE_VIEWER.customLayers.drawing;
                    this.$store.commit('SHOW_MAP_FEATURE', {
                        mapName: 'main',
                        customLayerName,
                        features: null,
                        feature: this.layerShape.featureObject.feature,
                        computedStyles,
                        styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selected,
                        reset: true
                    });
                }

                if (!this.wfsTModify) {
                    const margin = 100;
                    const popupWidth = PANEL_WIDTHS.FEEDBACK;
                    const marginRight = popupWidth + margin;
                    const currentResolution = this.getMainMap?.viewport?.resolution;

                    // Do not zoom to features on a small device.
                    if (!isSmallDevice()) {
                        let features = [this.layerShape.featureObject.feature];
                        this.$store.dispatch('zoomToFeatures', {
                            mapName: 'main',
                            features: features,
                            zoomWhenOutOfView: false,
                            options: {
                                padding: [margin, marginRight, margin, margin],
                                duration: DEFAULT_ANIMATION_MS
                            },
                            minResolution: currentResolution
                        });
                    }
                }
                this.lastwfsTAddress = null;
            },
            /**
             * Get the title for a input field.
             * @return {string}       [field title]
             */
            $_getFieldTitle(field) {
                return `${field.alias}`;
            },
            $_getValue(field) {
                return field.value || '';
            },
            $_getUserName() {
                if (this.isMobile) {
                    return this.userInfo.userName;
                } else {
                    return 'web-feedback';
                }
            },
            $_changedAttributes() {
                const changedAttributes = [];
                if (this.wfsTModify) {
                    this.orderedInformationAttributes.forEach((field) => {
                        const foundItem = this.layerShape.data[field.attribute];
                        if (foundItem !== field.value) {
                            if (Number.isInteger(field.value)) {
                                const num = field.value;
                                field.value = num.toString();
                            }
                            changedAttributes.push(field);
                        }
                    });
                }
                return changedAttributes.length > 0
                    ? changedAttributes
                    : this.orderedInformationAttributes;
            },

            /**
             * Perform a browser validity check
             * @return {string} [error string if not valide, otherwise null]
             */
            _validateUserInput() {
                const fieldsEmptyMessage = 'Niet alle velden zijn ingevuld';
                // reset error
                let error = null;
                // validate form
                if (this.$refs.wfstForm && !this.$refs.wfstForm.checkValidity()) {
                    error = fieldsEmptyMessage;
                }
                if (this.errorInUse) {
                    error = fieldsEmptyMessage;
                }
                return error;
            },
            /**
             * Save new or modified feedback
             */
            async $_onSubmit() {
                // Browser check input
                this.error = this._validateUserInput();
                if (this.error) {
                    this.$_showError();
                    return;
                }
                // get wfsT data
                const layerData = this._baseWfsTData();
                if (layerData) {
                    await this._saveWfsTData(layerData);
                }
            },
            /**
             * Save feedback data with a call to the API
             * @param  {object} layerData [feedback data object]
             * @return {[type]}              [description]
             */
            async _saveWfsTData(layerData) {
                this.isSaving = true;
                try {
                    // save feedback
                    let name = this.wfsTModify
                        ? 'UpdateFeedbackWfsT'
                        : 'CreateFeedbackWfsT';
                    const result = await this.$store.dispatch('useApiData', {
                        name: name,
                        data: layerData
                    });
                    const success = result.data === true;

                    if (this.isMobile && success) {
                        // Show success message
                        // if it is a new feedback, the message comes below the (feedback) buttons,
                        // by changing a feedback almost at the top with a small marge
                        this.$store.commit('SET_ALERT_MESSAGE', {
                            message: this.$t('feedbackProcessed'),
                            alertClass: layerData.id === '-1' ? 'lower' : null,
                            variant: 'success',
                            optional: false,
                            hasSearch: false
                        });

                        // hide message with delay
                        setTimeout(() => {
                            this.$store.commit('SET_ALERT_MESSAGE', {
                                message: null,
                                alertClass: null,
                                variant: null,
                                optional: null,
                                hasSearch: null
                            });
                        }, 5000);
                    }
                    this.isSaving = false;
                    let layerDataArray = [];
                    layerDataArray.push(layerData);
                    // close form
                    this.$emit('finished', {
                        layerData: layerDataArray,
                        themeObject: this.layerThemeObject
                    });
                } catch (error) {
                    this.showToast({
                        title: this.$t('saveFailureTitle'),
                        message: this.$t(error.message),
                        variant: 'danger',
                        autoHideDelay: 10000
                    });
                }
                this.isSaving = false;
            },
            /**
             * WfsT data compose start point
             * @return {object} [partial WfsT data object]
             */
            _baseWfsTData() {
                const layerTheme = this.layerThemeObject;

                if (layerTheme) {
                    const organizationId = this.isMobile
                        ? this.userInfo.organizationId
                        : this.getOrganizationId;
                    const informationAttributes = this.$_changedAttributes();

                    return getCreateWfsTData({
                        origInformationAttributes: informationAttributes,
                        layerTheme,
                        organizationId,
                        layerId: layerTheme.id,
                        featureObject: this.layerShape.featureObject,
                        geometryType: this.geometryType,
                        gpsLocation: this.getMainMap.gpsTracker.getPosition(),
                        userId: this.isMobile ? this.userInfo.id : -1,
                        userName: this.$_getUserName()
                    });
                }
                return null;
            },
            /**
             * Cancel add/modify feedback
             */
            $_onCancel(event) {
                this.$emit('cancel');
            },

            $_showError() {
                this.showToast({
                    message: this.$t('incorrectInputAllFields'),
                    variant: 'danger',
                    autoHideDelay: 10000
                });
            },
            showToast(message) {
                this.$store.commit('ADD_STATUS_MESSAGE', message);
            },
            /**
             * Initialize feedback data for a existing feedback (modify)
             */
            $_initializeModifyWfsTData() {
                // set id
                // modify feedback field initialisation
                const feature = this.layerShape.featureObject.feature;
                this.featureId = feature.id_;
                this.orderedInformationAttributes.forEach((field) => {
                    // Als eerste in de properties lijst
                    const foundItem = this.layerShape.data[field.attribute];
                    if (foundItem) {
                        field.value = foundItem;
                    } else if (field.attribute in feature.values_) {
                        // Ten tweede in de values lijst
                        field.value = feature.values_[field.attribute];
                    }
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
.modal-content {
    height: 100%;
    border: none;
    border-radius: initial;
}

.modal-body {
    padding: 0px;
    height: 100%;
}

.personal-data {
    padding-top: 10px;
}

.sticky-bottom {
    left: auto;
    background-color: #fff;
    padding-right: 40px;
    padding-left: 0px;
    padding-bottom: 20px;
}
</style>
